import { useMemo, useState } from "react"
import { createFileRoute, useNavigate, useParams } from "@tanstack/react-router"
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query"
import { toast } from "sonner"
import {
  BookOpen,
  FileText,
  HelpCircle,
  Hotel,
  Map as MapIcon,
  Pin,
  PinOff,
  Plane,
  ScrollText,
  ShieldAlert,
  Sparkles,
  Star,
  Trash2,
  UserSquare,
  X,
} from "lucide-react"
import {
  CollectionView,
  type CollectionConfig,
} from "@/components/collection/CollectionView"
import { downloadMappedCsv } from "@/lib/csv"
import { resolveEventId, useActiveEvent } from "@/core/active-event"
import { ApiError } from "@/core/api"
import {
  createEventDocument,
  deleteEventDocument,
  documentsKey,
  documentsQueryOptions,
  kindLabel,
  uploadEventDocumentFile,
  updateEventDocument,
  KIND_OPTIONS,
  type DocumentRow,
} from "@/core/documents"
import {
  Field,
  GhostButton,
  PrimaryButton,
  Select,
  TextInput,
} from "@/components/settings/primitives"

export const Route = createFileRoute("/$orgSlug/$eventSlug/documents")({
  loader: ({ context, params }) => {
    void resolveEventId(params.orgSlug, params.eventSlug).then((eventId) => {
      if (!eventId) return
      void context.queryClient
        .ensureQueryData(documentsQueryOptions(eventId))
        .catch(() => undefined)
    })
  },
  component: DocumentsView,
})

function KindIcon({ kind }: { kind: string }) {
  const cls = "h-4 w-4 shrink-0 text-plum-700/60 dark:text-plum-200/45"
  switch (kind) {
    case "emergency_info":
      return <ShieldAlert className={`${cls} text-rose-500 dark:text-rose-400`} />
    case "travel":
      return <Plane className={cls} />
    case "accommodation":
      return <Hotel className={cls} />
    case "speaker_bio":
      return <UserSquare className={cls} />
    case "venue":
      return <MapIcon className={cls} />
    case "policy":
      return <ScrollText className={cls} />
    case "faq":
      return <HelpCircle className={cls} />
    case "sponsor":
      return <Sparkles className={cls} />
    case "playbook":
      return <BookOpen className={cls} />
    default:
      return <FileText className={cls} />
  }
}

function isPublished(d: DocumentRow): boolean {
  return d.currentSnapshot != null
}
function updatedAt(d: DocumentRow): string {
  return d.modifiedAt ?? d.currentSnapshot?.publishedAt ?? d.createdAt ?? ""
}

function DocumentsView() {
  const { eventId } = useActiveEvent()
  const { orgSlug, eventSlug } = useParams({ from: "/$orgSlug/$eventSlug/documents" })
  const navigate = useNavigate()
  const qc = useQueryClient()
  const [creating, setCreating] = useState(false)

  const docsQ = useQuery({ ...documentsQueryOptions(eventId ?? ""), enabled: !!eventId })

  const invalidate = () => void qc.invalidateQueries({ queryKey: documentsKey(eventId ?? "") })
  const onError = (err: unknown) =>
    toast.error(err instanceof ApiError ? err.message : "Update failed")

  const pinMut = useMutation({
    mutationFn: (vars: { documentId: string; quickLink: boolean }) =>
      updateEventDocument(vars.documentId, { quickLink: vars.quickLink }),
    onSuccess: (_res, vars) => {
      toast.success(vars.quickLink ? "Pinned to Home" : "Unpinned from Home")
      invalidate()
    },
    onError,
  })

  const delMut = useMutation({
    mutationFn: (documentId: string) => deleteEventDocument(documentId),
    onSuccess: () => {
      toast.success("Document deleted")
      invalidate()
    },
    onError,
  })

  const open = (d: DocumentRow) =>
    void navigate({
      to: "/$orgSlug/$eventSlug/doc/$documentId",
      params: { orgSlug, eventSlug, documentId: d.id },
    })

  const documents = docsQ.data?.documents ?? []

  const config = useMemo<CollectionConfig<DocumentRow>>(() => {
    const exportCsv = (rows: DocumentRow[]) => {
      downloadMappedCsv(
        "documents.csv",
        ["Title", "Kind", "Status", "Pinned", "Updated"],
        rows,
        (d) => [
          d.title,
          kindLabel(d.kind),
          isPublished(d) ? `Published v${d.currentSnapshot!.version}` : "Draft",
          d.quickLink ? "yes" : "",
          updatedAt(d),
        ],
      )
    }

    return {
      title: "Documents",
      getId: (d) => d.id,
      searchText: (d) => `${d.title} ${d.slug} ${kindLabel(d.kind)}`,
      onOpen: open,
      onNew: () => setCreating(true),
      newLabel: "Upload document",
      defaultSortId: "updated",
      defaultSortDir: "desc",
      emptyTitle: "No documents",
      emptyBlurb:
        "Speaker bios, venue info, FAQs, and policies attendees can read from the app land here.",
      emptyIcon: <FileText className="h-5 w-5" />,
      savedViews: [
        { id: "all", label: "All", filter: () => true },
        { id: "published", label: "Published", filter: isPublished },
        { id: "draft", label: "Draft", filter: (d) => !isPublished(d) },
      ],
      groupOptions: [
        { id: "kind", label: "Kind", groupOf: (d) => kindLabel(d.kind) },
        {
          id: "status",
          label: "Status",
          groupOf: (d) => (isPublished(d) ? "Published" : "Draft"),
          order: (l) => (l === "Published" ? 0 : 1),
        },
      ],
      bulkActions: [
        {
          id: "pin",
          label: "Pin to Home",
          icon: <Pin className="h-4 w-4" />,
          run: (rows) =>
            rows
              .filter((d) => !d.quickLink)
              .forEach((d) => pinMut.mutate({ documentId: d.id, quickLink: true })),
        },
        {
          id: "unpin",
          label: "Unpin",
          icon: <PinOff className="h-4 w-4" />,
          run: (rows) =>
            rows
              .filter((d) => d.quickLink)
              .forEach((d) => pinMut.mutate({ documentId: d.id, quickLink: false })),
        },
        {
          id: "delete",
          label: "Delete",
          icon: <Trash2 className="h-4 w-4" />,
          run: (rows) => {
            if (confirm(`Delete ${rows.length} document${rows.length === 1 ? "" : "s"}?`))
              rows.forEach((d) => delMut.mutate(d.id))
          },
        },
        { id: "export", label: "Export", icon: <FileText className="h-4 w-4" />, run: exportCsv },
      ],
      columns: [
        {
          id: "title",
          header: "Name",
          sortable: true,
          sortValue: (d) => d.title.toLowerCase(),
          render: (d) => (
            <div className="flex items-center gap-2.5">
              <KindIcon kind={d.kind} />
              <span className="truncate font-semibold text-plum-900 dark:text-plum-50">
                {d.title || "Untitled document"}
              </span>
              {!!d.quickLink && (
                <Star className="h-3.5 w-3.5 shrink-0 fill-amber-400 text-amber-400" />
              )}
            </div>
          ),
        },
        {
          id: "kind",
          header: "Kind",
          className: "text-plum-700/75 dark:text-plum-200/60",
          sortable: true,
          sortValue: (d) => kindLabel(d.kind).toLowerCase(),
          render: (d) => kindLabel(d.kind),
        },
        {
          id: "status",
          header: "Status",
          render: (d) => {
            const published = isPublished(d)
            return (
              <span
                className={`rounded-md px-2 py-0.5 text-[11px] font-semibold ${
                  published
                    ? "bg-emerald-100 text-emerald-700 dark:bg-emerald-500/15 dark:text-emerald-400"
                    : "bg-amber-100 text-amber-700 dark:bg-amber-500/15 dark:text-amber-400"
                }`}
              >
                {published ? `Published · v${d.currentSnapshot!.version}` : "Draft"}
              </span>
            )
          },
        },
        {
          id: "pinned",
          header: "Pinned",
          render: (d) => {
            const pinned = !!d.quickLink
            return (
              <button
                onClick={(e) => {
                  e.stopPropagation()
                  if (pinMut.isPending) return
                  pinMut.mutate({ documentId: d.id, quickLink: !pinned })
                }}
                title={pinned ? "Unpin from Home" : "Pin to Home"}
                className="grid h-7 w-7 place-items-center rounded-md text-plum-700/40 transition hover:bg-plum-100/60 dark:text-plum-200/30 dark:hover:bg-white/5"
              >
                <Star
                  className={`h-4 w-4 ${
                    pinned ? "fill-amber-400 text-amber-400" : "text-plum-700/35 dark:text-plum-200/30"
                  }`}
                />
              </button>
            )
          },
        },
        {
          id: "updated",
          header: "Updated",
          className: "text-plum-700/55 dark:text-plum-200/40",
          sortable: true,
          sortValue: (d) => updatedAt(d),
          render: (d) => relTime(updatedAt(d)),
        },
      ],
    }
  }, [pinMut, delMut])

  return (
    <div className="relative h-full">
      <CollectionView
        config={config}
        rows={documents}
        loading={docsQ.isLoading && documents.length === 0}
        error={
          docsQ.isError ? ((docsQ.error as Error)?.message ?? "Failed to load documents") : null
        }
      />
      {creating && eventId && (
        <CreateDocumentOverlay
          eventId={eventId}
          onClose={() => setCreating(false)}
          onCreated={() => {
            setCreating(false)
            invalidate()
          }}
        />
      )}
    </div>
  )
}

function CreateDocumentOverlay({
  eventId,
  onClose,
  onCreated,
}: {
  eventId: string
  onClose: () => void
  onCreated: (doc: DocumentRow) => void
}) {
  const [title, setTitle] = useState("")
  const [kind, setKind] = useState("generic")
  const [file, setFile] = useState<File | null>(null)

  const create = useMutation({
    mutationFn: async () => {
      if (!file) throw new Error("Choose a document to upload")
      const mediaAssetId = await uploadEventDocumentFile(eventId, file)
      return createEventDocument({
        eventId,
        mediaAssetId,
        title: title.trim(),
        kind,
        attendeeVisible: true,
        status: "published",
      })
    },
    onSuccess: (res) => {
      toast.success("Document created")
      onCreated(res.document)
    },
    onError: (err) => toast.error(err instanceof ApiError ? err.message : "Create failed"),
  })

  const valid = title.trim().length > 0 && file != null
  const submit = () => {
    if (valid && !create.isPending) create.mutate()
  }

  return (
    <div className="absolute inset-0 z-40 flex items-start justify-center p-4 pt-[8vh]">
      <button
        type="button"
        aria-label="Close"
        onClick={onClose}
        className="absolute inset-0 cursor-default bg-plum-950/40 backdrop-blur-sm dark:bg-black/50"
      />
      <div className="relative z-10 flex max-h-full w-[min(94%,30rem)] flex-col overflow-hidden rounded-2xl bg-white shadow-fluent-lg ring-1 ring-plum-200/50 dark:bg-surface-raised dark:ring-white/10">
        <div className="flex items-center gap-2 border-b border-plum-200/50 px-5 py-3.5 dark:border-white/10">
          <span className="text-[var(--accent)] dark:text-plum-200/70">
            <FileText className="h-[18px] w-[18px]" />
          </span>
          <h2 className="text-[15px] font-semibold text-plum-900 dark:text-plum-50">Upload document</h2>
          <button
            onClick={onClose}
            className="ml-auto grid h-7 w-7 place-items-center rounded text-plum-700/55 transition hover:bg-plum-100/60 dark:text-plum-200/45 dark:hover:bg-white/5"
            title="Close"
          >
            <X className="h-4 w-4" />
          </button>
        </div>

        <form
          className="flex-1 space-y-4 overflow-y-auto scroll-thin px-5 py-4"
          onSubmit={(e) => {
            e.preventDefault()
            submit()
          }}
        >
          <div className="rounded-xl border border-plum-200/60 bg-plum-50/70 px-3.5 py-3 text-[12.5px] leading-5 text-plum-700/65 dark:border-white/10 dark:bg-white/[0.03] dark:text-plum-200/55">
            Documents are uploaded files for attendees to open or download. Use Pages for rich authored content.
          </div>
          <Field label="File">
            <input
              type="file"
              onChange={(event) => {
                const next = event.target.files?.[0] ?? null
                setFile(next)
                if (next && !title.trim()) {
                  setTitle(next.name.replace(/\.[^.]+$/, ""))
                }
              }}
              className="block w-full rounded-lg border border-plum-200/70 bg-white px-3 py-2 text-[13px] text-plum-900 file:mr-3 file:rounded-md file:border-0 file:bg-plum-100 file:px-3 file:py-1.5 file:text-[12px] file:font-semibold file:text-plum-800 dark:border-white/10 dark:bg-white/5 dark:text-plum-50 dark:file:bg-white/10 dark:file:text-plum-100"
            />
          </Field>
          <Field label="Title">
            <TextInput
              autoFocus
              value={title}
              placeholder="Welcome guide"
              onChange={(e) => setTitle(e.target.value)}
            />
          </Field>
          <Field label="Kind">
            <Select value={kind} onChange={(e) => setKind(e.target.value)}>
              {KIND_OPTIONS.map((k) => (
                <option key={k.value} value={k.value}>
                  {k.label}
                </option>
              ))}
            </Select>
          </Field>
          <button type="submit" className="hidden" />
        </form>

        <div className="flex items-center justify-end gap-2 border-t border-plum-200/50 px-5 py-3.5 dark:border-white/10">
          <GhostButton onClick={onClose}>Cancel</GhostButton>
          <PrimaryButton onClick={submit} disabled={!valid || create.isPending}>
            {create.isPending ? "Uploading…" : "Upload document"}
          </PrimaryButton>
        </div>
      </div>
    </div>
  )
}

function relTime(iso: string): string {
  if (!iso) return ""
  const t = Date.parse(iso.endsWith("Z") || iso.includes("+") ? iso : `${iso}Z`)
  if (Number.isNaN(t)) return ""
  const m = Math.round((Date.now() - t) / 60000)
  if (m < 1) return "just now"
  if (m < 60) return `${m}m ago`
  const h = Math.round(m / 60)
  if (h < 24) return `${h}h ago`
  return `${Math.round(h / 24)}d ago`
}
